import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import client from '../lib/api';
import { providersFromResponse } from '../lib/providerLogos';

const PAGE_SIZE = 60;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Bir kategorinin oyun kataloğunu sunucu taraflı filtre + sayfalama ile yönetir.
 *
 * Katalog binlerce oyuna ulaştığı için filtreleme istemcide yapılamaz: yalnızca
 * yüklenmiş sayfayı süzmek yanlış sonuç verir. Bu yüzden sağlayıcı, vitrin
 * etiketi ve arama sunucuya gönderilir; filtre değişince liste baştan çekilir.
 */
export const useGameCatalog = (category, options = {}) => {
  // ``flag`` verilirse vitrin etiketi sayfanın elindedir (Slotlar sekmesi
  // adresten okunur, bkz. lib/slotTabs.js); verilmezse kanca kendisi tutar.
  const { pageSize = PAGE_SIZE, keywords = '', flag: controlledFlag } = options;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [providers, setProviders] = useState([]);
  // Sağlayıcı süzgeci adres çubuğunda taşınır: ana sayfanın "Popüler
  // Sağlayıcılar" şeridi `/slotlar?provider=<slug>` adresine bağlanır ve
  // parametre okunmazsa bağlantı sessizce filtresiz sayfa açardı. Adreste
  // durması ayrıca süzgeci paylaşılabilir ve geri tuşuyla geri alınabilir yapar.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProvider = searchParams.get('provider') || 'all';
  const [provider, setProviderState] = useState(urlProvider);
  const [flagState, setFlag] = useState('all');
  const flag = controlledFlag ?? flagState;
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  // Yalnızca en son isteğin sonucu uygulanır (yarış koşulu koruması).
  const requestId = useRef(0);

  // Adres dışarıdan değişince (sağlayıcı şeridi, geri/ileri) süzgeç ona uyar.
  useEffect(() => { setProviderState(urlProvider); }, [urlProvider]);

  const setProvider = useCallback((value) => {
    const next = value || 'all';
    setProviderState(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'all') params.delete('provider');
      else params.set('provider', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Her tuş vuruşunda istek atılmasın diye arama metni geciktirilir.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  const buildParams = useCallback((skip) => {
    const params = { category, skip, limit: pageSize };
    if (provider !== 'all') params.provider = provider;
    if (flag !== 'all') params.flag = flag;
    if (term) params.q = term;
    if (keywords) params.keywords = keywords;
    return params;
  }, [category, flag, keywords, pageSize, provider, term]);

  useEffect(() => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setLoadingMore(false);
    setError('');
    client.get('/games', { params: buildParams(0) })
      .then(({ data }) => {
        if (id !== requestId.current) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setItems([]);
        setTotal(0);
        setHasMore(false);
        setError('Oyunlar yüklenemedi. Lütfen tekrar deneyin.');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [buildParams]);

  // Sağlayıcı çipleri kategoriye bağlıdır, filtrelerden bağımsız yüklenir.
  // Uç görünen ad ve logoyu `providers` altında verir; eski sürümle uyum
  // için ham slug listesi (`items`) da desteklenir.
  useEffect(() => {
    let active = true;
    client.get('/game-providers', { params: { category } })
      .then(({ data }) => {
        if (!active) return;
        setProviders(providersFromResponse(data, Number.POSITIVE_INFINITY));
      })
      .catch(() => { if (active) setProviders([]); });
    return () => { active = false; };
  }, [category]);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore) return;
    const id = requestId.current;
    setLoadingMore(true);
    client.get('/games', { params: buildParams(items.length) })
      .then(({ data }) => {
        if (id !== requestId.current) return;
        setItems((prev) => [...prev, ...(data.items || [])]);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      })
      .catch(() => {
        if (id === requestId.current) setError('Sonraki sayfa yüklenemedi.');
      })
      .finally(() => {
        if (id === requestId.current) setLoadingMore(false);
      });
  }, [buildParams, hasMore, items.length, loading, loadingMore]);

  const reset = useCallback(() => {
    setProvider('all');
    setFlag('all');
    setQ('');
  }, [setProvider]);

  return {
    items, total, hasMore, loading, loadingMore, error,
    providers, provider, setProvider,
    flag, setFlag, q, setQ, loadMore, reset,
  };
};

/** Oyun kaydını GameTile'ın beklediği biçime çevirir. */
export const mapForTile = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

export default useGameCatalog;
